<template>
  <div class="emh-side-filters-vehicle-category">
    <emh-side-filters-category-filter-template
      v-for="filter in multiselectFilters"
      :key="filter.name"
      :title="filterCategory.hideFilterTitles ? undefined : filter.name"
      :filter-counter="getSelectedCategoryFilterCounter(filter)"
      data-test-id="emh-side-filters-category-filter-template__vehicle-template"
      @clear-category="onClearCategory(filter)"
    >
      <ui-multiselect-group
        v-if="filter.code === FilterCode.bodyType"
        class="emh-side-filters-vehicle-category__options"
        :options="filter.options"
        data-test-id="emh-side-filters-vehicle-category__vehicle-options"
        @select-option="filter.handleChange"
      />
      <ui-checkbox-list
        v-else
        :name="filter.name"
        :title="filter.title"
        :options="filter.options"
        :max-items="filter.options.length"
        @change="filter.handleChange"
      />
    </emh-side-filters-category-filter-template>
    <emh-side-filters-category-filter-template
      v-if="motorisationFilter"
      :key="motorisationFilter.name"
      :title="motorisationFilter.name"
      :filter-counter="getSelectedCategoryFilterCounter(motorisationFilter)"
      data-test-id="emh-side-filters-category-filter-template__vehicle-options"
      @clear-category="onClearCategory(motorisationFilter)"
    >
      <ui-checkbox-list
        :name="motorisationFilter.name"
        :title="motorisationFilter.title"
        :options="motorisationFilter.options"
        :max-items="maxItems"
        @change="motorisationFilter.handleChange"
      />
    </emh-side-filters-category-filter-template>
  </div>
</template>

<script lang="ts">
export default {
  name: 'EmhSideFiltersVehicleCategory',
};
</script>

<script lang="ts" setup>
import { infuse } from '@dh-io-emh/shared/src/ioc-config';
import ProductFilterBase, {
  FilterCode,
  IProductFilterGroup,
} from '@dh-io-emh/shared/src/models/product-filter/product-filter-base.model';
import UiCheckboxList from '@dh-io-emh/ui/src/form/checkbox/checkboxList/CheckboxList.vue';
import { MultiselectGroupOption } from '@dh-io-emh/ui/src/form/multiselectGroup/MultiselectGroup.model';
import UiMultiselectGroup from '@dh-io-emh/ui/src/form/multiselectGroup/MultiselectGroup.vue';
import { computed, PropType, toRefs } from 'vue';
import {
  getSelectedCategoryFilterCounter,
  useMultiselectFilterCategories,
} from '../../../composables/filter-categories.composable';
import EmhSideFiltersCategoryFilterTemplate from '../sideFiltersCategoryFilterTemplate/SideFiltersCategoryFilterTemplate.vue';

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: true,
  },
  filterCategory: {
    type: Object as PropType<IProductFilterGroup>,
    required: true,
  },
});

const emit = defineEmits(['change', 'clear-category']);

const { filterCategory, isLoading } = toRefs(props);
const { hideZeroCountMultiSelectFilter } = infuse('config');

const filters = useMultiselectFilterCategories({
  filterCategory,
  emit,
  exclude: hideZeroCountMultiSelectFilter ? [{ key: 'count', value: 0 }] : undefined,
  optionMappingFn: (option: MultiselectGroupOption, filter: ProductFilterBase) => {
    option = { ...option, disabled: isLoading.value };

    if (filter.code === FilterCode.bodyType) {
      try {
        return {
          ...option,
          imageUrl: new URL(`../../../assets/sideFilters/bodyTypeImages/${option.value}.png`, import.meta.url).href,
        };
      } catch (error) {
        return option;
      }
    } else if (filter.code === FilterCode.brand) {
      try {
        return {
          ...option,
          label: '',
          imageUrl: new URL(`../../../assets/sideFilters/brands/${option.value.toLowerCase()}.svg`, import.meta.url)
            .href,
        };
      } catch (error) {
        return { ...option, label: '' };
      }
    }

    return option;
  },
});

const multiselectFilters = computed(() => filters?.value.filter(({ code }) => code !== FilterCode.modelDesignation));
const motorisationFilter = computed(() => filters?.value.find(({ code }) => code === FilterCode.modelDesignation));

const onClearCategory = (filterOption) => emit('clear-category', [filterOption.code]);
</script>

<style lang="scss" scoped>
.emh-side-filters-vehicle-category {
  :deep(.emh-ui-multiselect-group) {
    grid-template-columns: repeat(2, minmax(72px, 1fr));
  }
}
</style>
